import { Camera, Color, DoubleSide, Mesh, PlaneGeometry, ShaderMaterial, Vector2, Vector3 } from 'three';

const vertexShader = /* glsl */ `
uniform vec3 uCameraLocal;
varying vec3 vLocalPosition;

void main() {
    // Snap plane to camera XY so the grid follows the view and is truly infinite.
    // Z stays at mesh position (ground level). Grid lines use the mesh's own XY
    // (world XY unless the grid sits in a turned parent) so they remain stable.
    vec3 pos = position;
    pos.x += uCameraLocal.x;
    pos.y += uCameraLocal.y;
    vLocalPosition = pos;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

const fragmentShader = /* glsl */ `
uniform vec3 uCameraLocal;
varying vec3 vLocalPosition;

uniform float uGridSize1;
uniform float uGridSize2;
uniform float uGridSize3;
uniform vec3 uGridColor;
uniform vec3 uAxisColorX;
uniform vec3 uAxisColorY;
uniform float uFadeDistance;
uniform float uMinorOpacity;
uniform float uMajorOpacity;
uniform float uSuperMajorOpacity;
uniform float uAxisOpacity;
uniform vec3 uGroundColor;
uniform float uGroundOpacity;
uniform vec3 uFogColor;
uniform float uFogOpacity;
uniform vec2 uAxisCenter;

void main() {
    vec2 coord = vLocalPosition.xy;

    // Screen-space derivatives for anti-aliased lines
    vec2 dCoord = fwidth(coord);

    // Minor grid lines
    vec2 grid1 = abs(fract(coord / uGridSize1 - 0.5) - 0.5) / (dCoord / uGridSize1);
    float line1 = 1.0 - min(min(grid1.x, grid1.y), 1.0);

    // Major grid lines
    vec2 grid2 = abs(fract(coord / uGridSize2 - 0.5) - 0.5) / (dCoord / uGridSize2);
    float line2 = 1.0 - min(min(grid2.x, grid2.y), 1.0);

    // Super-major grid lines (visible at far distances)
    vec2 grid3 = abs(fract(coord / uGridSize3 - 0.5) - 0.5) / (dCoord / uGridSize3);
    float line3 = 1.0 - min(min(grid3.x, grid3.y), 1.0);

    // Distance-based fade from camera XY position
    float dist = length(coord - uCameraLocal.xy);
    float fade = 1.0 - smoothstep(uFadeDistance * 0.5, uFadeDistance, dist);

    // LOD fade: minor lines fade out earlier, super-major fades in at distance
    float minorFade = 1.0 - smoothstep(uFadeDistance * 0.15, uFadeDistance * 0.4, dist);
    float superMajorFade = smoothstep(uFadeDistance * 0.05, uFadeDistance * 0.25, dist);

    // Fog: ground color blends toward fog color at distance
    float fogFactor = smoothstep(uFadeDistance * 0.2, uFadeDistance * 0.8, dist);
    vec3 groundWithFog = mix(uGroundColor, uFogColor, fogFactor * uFogOpacity);

    // Start with ground plane color (with fog applied)
    vec3 color = groundWithFog;
    float alpha = uGroundOpacity * fade;

    // Blend grid lines on top of ground with LOD
    float lineAlpha = (line1 * uMinorOpacity * minorFade + line2 * uMajorOpacity + line3 * uSuperMajorOpacity * superMajorFade) * fade;
    color = mix(color, uGridColor, lineAlpha / max(alpha + lineAlpha, 0.001));
    alpha = max(alpha, lineAlpha);

    // X-axis line (red) centered on model
    float xAxisThreshold = dCoord.y * 2.0;
    float xAxisStrength = 1.0 - smoothstep(0.0, xAxisThreshold, abs(coord.y - uAxisCenter.y));
    if (xAxisStrength > 0.0) {
        color = mix(color, uAxisColorX, xAxisStrength);
        alpha = max(alpha, uAxisOpacity * fade * xAxisStrength);
    }

    // Y-axis line (green) centered on model
    float yAxisThreshold = dCoord.x * 2.0;
    float yAxisStrength = 1.0 - smoothstep(0.0, yAxisThreshold, abs(coord.x - uAxisCenter.x));
    if (yAxisStrength > 0.0) {
        color = mix(color, uAxisColorY, yAxisStrength);
        alpha = max(alpha, uAxisOpacity * fade * yAxisStrength);
    }

    if (alpha < 0.001) discard;

    gl_FragColor = vec4(color, alpha);
}
`;

export class InfiniteGridHelper extends Mesh<PlaneGeometry, ShaderMaterial> {

  constructor(gridSize1 = 10, gridSize2 = 100, fadeDistance = 1000) {
    const geometry = new PlaneGeometry(fadeDistance * 4, fadeDistance * 4);
    const material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uGridSize1:         { value: gridSize1 },
        uGridSize2:         { value: gridSize2 },
        uGridSize3:         { value: gridSize2 * 10 },
        uGridColor:         { value: new Color('#2a2a2a') },
        uAxisColorX:        { value: new Color('#d01030') },
        uAxisColorY:        { value: new Color('#3f8a00') },
        uFadeDistance:       { value: fadeDistance },
        uMinorOpacity:      { value: 0.45 },
        uMajorOpacity:      { value: 0.75 },
        uSuperMajorOpacity: { value: 0.9 },
        uAxisOpacity:       { value: 0.8 },
        uGroundColor:       { value: new Color('#ffffff') },
        uGroundOpacity:     { value: 0.7 },
        uFogColor:          { value: new Color('#d0d8e0') },
        uFogOpacity:        { value: 0.6 },
        uAxisCenter:        { value: new Vector2(0, 0) },
        uCameraLocal:       { value: new Vector3() },
      },
      transparent: true,
      side: DoubleSide,
      depthWrite: false,
    });

    super(geometry, material);
    this.name = 'InfiniteGridHelper';
    this.frustumCulled = false;
    this.renderOrder = -1;
  }

  override onBeforeRender(...args: Parameters<Mesh['onBeforeRender']>): void {
    const camera: Camera = args[2];
    const cameraLocal = this.material.uniforms['uCameraLocal'].value as Vector3;
    this.worldToLocal(camera.getWorldPosition(cameraLocal));
  }

  /** Update grid spacing and fade distance based on scene scale */
  updateScale(maxSize: number) {
    // Auto-adapt grid spacing: round down to nearest power of 10, then divide by 5
    const order = Math.floor(Math.log10(Math.max(maxSize, 1)));
    const gridUnit = Math.pow(10, order) / 5;
    const majorUnit = gridUnit * 10;
    const fadeDistance = maxSize * 16;

    this.material.uniforms['uGridSize1'].value = gridUnit;
    this.material.uniforms['uGridSize2'].value = majorUnit;
    this.material.uniforms['uGridSize3'].value = majorUnit * 10;
    this.material.uniforms['uFadeDistance'].value = fadeDistance;

    // Resize plane to cover fade area (plane follows camera, so this only needs to fill the viewport)
    const planeSize = fadeDistance * 4;
    this.geometry.dispose();
    this.geometry = new PlaneGeometry(planeSize, planeSize);
  }

  /** Set the center point for axis lines (model center XY) */
  setAxisCenter(x: number, y: number) {
    this.material.uniforms['uAxisCenter'].value.set(x, y);
  }

  /** Set dark or light theme */
  setTheme(isDark: boolean) {
    const u = this.material.uniforms;
    u['uGridColor'].value.set(isDark ? '#e0e0e0' : '#2a2a2a');
    u['uAxisColorX'].value.set(isDark ? '#ff5a6e' : '#d01030');
    u['uAxisColorY'].value.set(isDark ? '#a8e65a' : '#3f8a00');
    u['uGroundColor'].value.set(isDark ? '#1e1e1e' : '#ffffff');
    u['uFogColor'].value.set(isDark ? '#0a0e14' : '#d0d8e0');
    u['uMinorOpacity'].value = isDark ? 0.45 : 0.45;
    u['uMajorOpacity'].value = isDark ? 0.75 : 0.75;
    u['uSuperMajorOpacity'].value = isDark ? 0.9 : 0.9;
  }

  override dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    super.dispose();
  }
}
